import { ArrowUpRight, Check } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";
import { waLink } from "@/lib/contact";

const STRENGTH_IMG =
  "https://images.unsplash.com/photo-1758875569256-f37c438cac65?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxhdGhsZXRlJTIwdHJhaW5pbmclMjBneW18ZW58MHx8fHwxNzkyOTUwODUzfDA&ixlib=rb-4.1.0&q=85";

const packages = [
  {
    name: "Foundation",
    slug: "foundation",
    duration: "4 weeks",
    price: "₹4,999",
    blurb: "Build your base — structure, technique and consistency from day one.",
    features: [
      "Personalised training program",
      "Exercise video library",
      "Weekly check-in",
      "1 form review per week",
    ],
  },
  {
    name: "Performance",
    slug: "performance",
    duration: "4 weeks",
    price: "₹6,999",
    blurb: "Periodized programming for lifters and athletes chasing real numbers.",
    features: [
      "Periodized strength program",
      "Bi-weekly video calls",
      "Unlimited form reviews",
      "Mobility & recovery protocols",
    ],
    featured: true,
  },
  {
    name: "Elite",
    slug: "elite",
    duration: "4 weeks",
    price: "₹11,999",
    blurb: "Fully coached. Every session, every rep, every detail handled.",
    features: [
      "1:1 coaching with Keshav",
      "Weekly video calls",
      "Nutrition guidance",
      "Competition & event prep",
    ],
  },
];

export default function Packages() {
  return (
    <section id="strength" className="py-24 md:py-36 bg-[#efe9dd] border-y border-[#E5E0D8]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-16 md:mb-20">
          <div className="lg:col-span-7">
            <Reveal>
              <SectionLabel>Strength & Conditioning</SectionLabel>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display font-light text-[#111111] text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight mt-6">
                Programs that make you
                <span className="italic text-[#8DA37F]"> undeniably stronger.</span>
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.18}>
              <p className="text-base md:text-lg leading-relaxed text-[#111111]/70 max-w-md">
                Remote coaching built around your equipment, your schedule and
                your injury history — not a cookie-cutter split.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {packages.map((pkg, i) => (
            <Reveal key={pkg.slug} delay={i * 0.1} className="h-full">
              <div
                data-testid={`package-card-${pkg.slug}`}
                className={`relative h-full flex flex-col border p-8 md:p-10 transition-transform duration-500 hover:-translate-y-2 ${
                  pkg.featured
                    ? "bg-[#111111] text-[#F5F0E8] border-[#111111]"
                    : "bg-[#F5F0E8] text-[#111111] border-[#E5E0D8]"
                }`}
              >
                {pkg.featured && (
                  <span className="absolute -top-3 left-8 bg-[#8DA37F] text-[#111111] text-[10px] tracking-[0.2em] uppercase font-semibold px-4 py-1.5">
                    Most chosen
                  </span>
                )}
                <p className={`text-xs tracking-[0.25em] uppercase font-medium ${pkg.featured ? "text-[#8DA37F]" : "text-[#6b7f5e]"}`}>
                  {pkg.duration}
                </p>
                <h3 className="font-display text-4xl mt-3 tracking-tight">{pkg.name}</h3>
                <p className={`mt-3 text-sm leading-relaxed ${pkg.featured ? "text-[#F5F0E8]/65" : "text-[#111111]/60"}`}>
                  {pkg.blurb}
                </p>
                <p className="mt-7 font-display text-5xl tracking-tight">
                  {pkg.price}
                </p>
                <ul className="mt-8 space-y-3.5 flex-1">
                  {pkg.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm">
                      <Check className={`h-4 w-4 mt-0.5 shrink-0 ${pkg.featured ? "text-[#8DA37F]" : "text-[#6b7f5e]"}`} />
                      <span className={pkg.featured ? "text-[#F5F0E8]/85" : "text-[#111111]/80"}>{f}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={waLink(`Hi Physiora, I'm interested in the ${pkg.name} strength & conditioning package (${pkg.duration}).`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`package-enquire-btn-${pkg.slug}`}
                  className={`mt-9 inline-flex items-center justify-center gap-2 text-xs tracking-[0.15em] uppercase font-medium px-7 py-4 rounded-full transition-all duration-300 hover:-translate-y-0.5 ${
                    pkg.featured
                      ? "bg-[#8DA37F] text-[#111111] hover:bg-[#F5F0E8]"
                      : "bg-[#111111] text-[#F5F0E8] hover:bg-[#8DA37F] hover:text-[#111111]"
                  }`}
                >
                  Enquire
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-16 md:mt-20 relative overflow-hidden">
            <img
              src={STRENGTH_IMG}
              alt="Athlete training with a coach in a gym"
              className="w-full h-56 md:h-72 object-cover object-center sepia-[0.12]"
              data-testid="strength-image"
            />
            <div className="absolute inset-0 bg-[#111111]/35" />
            <p className="absolute inset-0 flex items-center justify-center font-display italic text-3xl md:text-5xl text-[#F5F0E8] tracking-tight">
              Strong is a skill. We teach it.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
